
import {
  defineComponent,
  onMounted,
  reactive,
  watch,
  ref,
  toRefs
} from 'vue'
import LangSelect from '@/components/lang_select/Index.vue'
import { useRoute, LocationQuery, useRouter } from 'vue-router'
import { useStore } from '@/store'
import { UserActionTypes } from '@/store/modules/user/action-types'
import { useI18n } from 'vue-i18n'
import Logo from '@/assets/images/login/carpenter_logo_shape_large.png'
import { generateMfaQrCode, loginVerify } from '@/apis/user'
import { RequestParams } from 'axios-mapper'
import { ElMessage } from 'element-plus'

export default defineComponent({
  components: {
    LangSelect
  },
  setup() {
    const userNameRef = ref(null)
    const passwordRef = ref(null)
    const loginFormRef = ref(null)
    const mfaFormRef = ref(null)
    const mfaCodeRef = ref(null)
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const { t } = useI18n()
    const temporaryToken = ref(null as string | null)
    const state = reactive({
      loginForm: {
        username: '',
        password: ''
      },
      loginRules: {
        username: [{ validator: userNameRef, trigger: 'blur' }],
        password: [{ validator: passwordRef, trigger: 'blur' }]
      },
      mfaForm: {
        mfaCode: ''
      },
      mfaFormStruct: [
        { prop: 'comment', label: null, component: 'el-input', type: 'text', model: '' }
      ],
      mfaFormRules: {
        mfaCode: [{ required: true, message: t('mfa.mfaChecksumP'), trigger: 'change' }]
      },
      passwordType: 'password',
      qrCodeSrc: '' as string | null,
      loading: false,
      step: 1,
      capsTooltip: false,
      redirect: '',
      otherQuery: {}
    })

    function tokenExpiredTip() {
      ElMessage({
        message: 'token 过期,请重新登录',
        type: 'error',
        duration: 2000
      })
      state.step = 1
    }

    const methods = reactive({
      checkCapslock: (e: KeyboardEvent) => {
        if (e && typeof e.getModifierState === 'function') {
          state.capsTooltip = e.getModifierState('CapsLock')
        }
      },
      async handleLoginVerify() {
        try {
          await (loginFormRef.value as any).validate()
          state.loading = true
          const params = {
            userAccount: state.loginForm.username,
            userPassword: state.loginForm.password
          }
          const data = await loginVerify(params)
          if (data && data.code === 200 && data.data) {
            temporaryToken.value = data.data.temporaryToken
            if (data.data.isBindMFA) {
              state.qrCodeSrc = null
              state.step = 2
            } else {
              const param: RequestParams = {
                width: 300,
                height: 300
              }
              state.qrCodeSrc = await generateMfaQrCode(data.data.temporaryToken, param)
              state.step = 3
            }
          } else if (data && data.code !== 200) {
            const errorMsg = data.msg ? data.msg : '登录失败'
            ElMessage({
              message: errorMsg,
              type: 'error',
              duration: 2000
            })
          }
          // Just to simulate the time of the request
          setTimeout(() => {
            state.loading = false
          }, 0.5 * 1000)
        } catch (error) {
          console.log(error)
        }
      },
      async handleBind() {
        await (mfaFormRef.value as any).validate()
        if (!temporaryToken.value) {
          tokenExpiredTip()
          return
        }
        const mfaInfo = {
          temporaryToken: temporaryToken.value,
          mfaCode: state.mfaForm.mfaCode
        }
        try {
          await store.dispatch(UserActionTypes.ACTION_BIND_MFA_LOGIN, mfaInfo)
        } catch (err) {
          ElMessage({
            message: err,
            type: 'error',
            duration: 2000
          })
        }
        router.push({
          path: state.redirect || '/',
          query: state.otherQuery
        }).catch(err => {
          console.log(err)
        })
      },
      async handleVerifyMFA() {
        await (mfaFormRef.value as any).validate()
        if (!temporaryToken.value) {
          tokenExpiredTip()
          return
        }
        const mfaInfo = {
          temporaryToken: temporaryToken.value,
          mfaCode: state.mfaForm.mfaCode
        }
        try {
          await store.dispatch(UserActionTypes.ACTION_MFA_LOGIN, mfaInfo)
        } catch (err) {
          ElMessage({
            message: err,
            type: 'error',
            duration: 2000
          })
        }
        router.push({
          path: state.redirect || '/',
          query: state.otherQuery
        }).catch(err => {
          console.log(err)
        })
      },
      async handleBack() {
        if (state.step === 2 || state.step === 3) {
          state.step = 1
        }
      }
    })

    function getOtherQuery(query: LocationQuery) {
      return Object.keys(query).reduce((acc, cur) => {
        if (cur !== 'redirect') {
          acc[cur] = query[cur]
        }
        return acc
      }, {} as LocationQuery)
    }

    watch(() => route.query, query => {
      if (query) {
        state.redirect = query.redirect?.toString() ?? ''
        state.otherQuery = getOtherQuery(query)
      }
    })

    onMounted(() => {
      if (state.loginForm.username === '') {
        (userNameRef.value as any).focus()
      } else if (state.loginForm.password === '') {
        (passwordRef.value as any).focus()
      } else if (state.mfaForm.mfaCode === '') {
        (mfaCodeRef.value as any).focus()
      }
      const query = route.query
      if (query) {
        state.redirect = query.redirect?.toString() ?? ''
        state.otherQuery = getOtherQuery(query)
      }
    })

    return {
      userNameRef,
      passwordRef,
      loginFormRef,
      mfaFormRef,
      mfaCodeRef,
      Logo,
      ...toRefs(state),
      ...toRefs(methods),
      t
    }
  }
})
